import { parseInline } from './inlineParser.js';
function count(s, char) {
    let cnt = 0;
    for (const c of s)
        if (c === char)
            cnt++;
    return cnt;
}
function findHeaderEnd(arr) {
    return arr.findIndex((e) => /\+(=+\+)+/.test(e));
}
function findHeaderLines(arr, headerInd) {
    return [headerInd].concat(arr
        .map((e, ind) => (ind > headerInd && /^\+(-+\+)+$/.test(e) ? ind : null))
        .filter((e) => e !== null));
}
function pairwise(arr, func) {
    for (let i = 0; i < arr.length - 1; i++) {
        func(arr[i], arr[i + 1]);
    }
}
function parseHeaderCells(header, numHeaders) {
    const headerStr = Array.from(Array(numHeaders)).fill('');
    for (const line of header) {
        const headerLineStr = line.split('|').slice(1, -1);
        for (const [ind, str] of headerLineStr.entries()) {
            if (str) {
                const r = ind % numHeaders;
                const addition = str.trim();
                if (addition) {
                    headerStr[r] += `${addition}\n`;
                }
            }
        }
    }
    return headerStr;
}
function fg(s) {
    const ind = s
        .replace(/[^\+\-]-[^\+\-]/g, '')
        .replace(/[^\+\-\|]/g, '')
        .replace(/\+\-+\+/g, 'x');
    return Object.fromEntries(ind.split('').map((e, index) => [index, e === '|']));
}
function parseCells(content, headerInd) {
    const patt = /^\+(\-+\+)+[^\n]*\|$/;
    let cells = [];
    function parseRow(i, j) {
        const row = content.slice(i + 1, j);
        if (row.length === 0)
            return;
        const isSpecial = row.some((e) => patt.test(e));
        let lastColLookup = null;
        if (!isSpecial) {
            const numCols = count(row[0], '|') - 1;
            const ret = Array.from(Array(numCols)).fill('');
            for (const line of row) {
                const lineStr = line.split(/(?<!\\)\|/).slice(1, -1);
                for (const [ind, str] of lineStr.entries()) {
                    if (str) {
                        const r = ind % numCols;
                        ret[r] += `${str}\n`;
                    }
                }
            }
            cells.push(ret);
        }
        else {
            const maxNumCols = Math.max(...row.map((e) => count(e, '|') - 1));
            const subCells = [];
            const ret = Array.from(Array(maxNumCols)).fill('');
            for (const line of row) {
                const isLineSpecial = patt.test(line);
                const colLookup = isLineSpecial ? fg(line) : null;
                const lineStr = line
                    .replace(/\+-+\+ ?/g, '|')
                    .split(/(?<!\\)\|/)
                    .slice(1, -1);
                let newSubRow = null;
                let c = 0;
                for (const [ind, str] of lineStr.entries()) {
                    const isEmpty = !str || str.trim() === '';
                    if (isEmpty)
                        continue;
                    const r = ind % maxNumCols;
                    if (str.length > 0) {
                        if (lastColLookup && !lastColLookup[r]) {
                            newSubRow = newSubRow || Array.from(Array(maxNumCols)).fill('');
                            newSubRow[r] += `${str}\n`;
                        }
                        else {
                            if (colLookup && !colLookup[r])
                                c++;
                            ret[r + c] += `${str}\n`;
                        }
                    }
                }
                lastColLookup = colLookup;
                if (newSubRow !== null)
                    subCells.push(newSubRow.map((e) => (e === '' ? null : e)));
            }
            cells.push(ret);
            cells = cells.concat(subCells);
        }
    }
    const headerLines = findHeaderLines(content, headerInd);
    pairwise(headerLines, (i, j) => {
        parseRow(i, j);
    });
    return cells;
}
function escapePipes(s) {
    let r = s.replace(/(\[\[[^ ]*)\|([^ ]*]\])/, '$1\u1D2A$2');
    return r.replace(/([a-zA-Z0-9])(\|)+([a-zA-Z0-9])/, '$1\\$2$3');
}
function unescapePipes(s) {
    let r = s.replace(/\u1D2A/g, '|');
    return r.replace(/([a-zA-Z0-9])(\\\|)+([a-zA-Z0-9])/, '$1|$3');
}
function parseCellContent(text) {
    // Parse inline markup within a table cell, using single-quote links
    return parseInline(text, { singleQuoteLinks: true });
}
function parseCellWithLists(text) {
    // Check if cell content contains list items (lines starting with "- ")
    const lines = text.split('\n').filter((l) => l.length > 0);
    const hasListItems = lines.some((l) => l.trimStart().startsWith('- '));
    if (hasListItems) {
        const listItems = [];
        const nonListLines = [];
        for (const line of lines) {
            const trimmed = line.trimStart();
            if (trimmed.startsWith('- ')) {
                listItems.push(trimmed.slice(2));
            }
            else {
                nonListLines.push(line);
            }
        }
        const listHtml = '\n<ul>\n' +
            listItems.map((item) => `\t<li><p>${parseCellContent(item)}</p></li>`).join('\n') +
            '\n</ul>\n';
        const prefix = nonListLines.length > 0
            ? nonListLines.map((l) => parseCellContent(l)).join(' ')
            : '';
        return prefix + listHtml;
    }
    // No lists, just parse inline content
    return parseCellContent(text);
}
function parseTHContent(text) {
    if (!text)
        return '';
    return parseCellContent(text);
}
export function parseTable(raw) {
    const table = escapePipes(raw);
    const arr = table.split('\n').filter((e) => e);
    const headerInd = findHeaderEnd(arr);
    const firstBorder = arr.find((l) => l.includes('+'));
    const numHeaders = firstBorder ? count(firstBorder, '+') - 1 : count(arr[0], '|') - 1;
    const header = arr.slice(1, headerInd);
    const parsedHeader = parseHeaderCells(header, numHeaders);
    const parsedCells = parseCells(arr, headerInd);
    const cellStr = parsedCells
        .map((row) => `<tr>${row
        .filter((v) => v && v.trim() !== '')
        .map((v) => {
        const content = unescapePipes(v);
        const parsed = parseCellWithLists(content);
        // Determine if cell has complex content (lists)
        if (parsed.includes('<ul>')) {
            return `<td>\n<p>\n${parsed}\n</p>\n</td>`;
        }
        return `<td><p>${parsed}</p></td>`;
    })
        .join('')}</tr>`)
        .join('');
    const headerStr = parsedHeader
        .map((e) => `<th><p>\n${parseTHContent(e.trim())}\n</p></th>\n`)
        .join('');
    return `\n  <table>\n  <thead>\n  <tr>\n  ${headerStr}\n  </tr>\n  </thead>\n\n        <tbody>\n            ${cellStr}\n        </tbody>\n        </table>\n\n`;
}
